import React from 'react';
import Modal from '@material-ui/core/Modal';
import {
  makeStyles,
  Typography,
  IconButton,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import PerfectScrollbar from 'react-perfect-scrollbar';
import { TapTapStatusReport } from 'src/redux/slices/taptap-customer/types';
import { formatDate } from 'src/utils';
import { useNavigate } from 'react-router';

const useStyles = makeStyles((theme) => ({
  modal: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(1)
  },
  body: {
    position: 'relative',
    width: '95%',
    maxWidth: 1000,
    maxHeight: '80vh',
    backgroundColor: theme.palette.background.paper,
    padding: theme.spacing(3),
    borderRadius: 8,
    outline: 'none',
    boxSizing: 'border-box',
    overflowY: 'hidden'
  },
  titleContainer: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(1)
  },
  closeButton: {
    padding: 0,
    marginTop: -theme.spacing(0.5),
    marginRight: -theme.spacing(1)
  },
  formContainer: {
    marginTop: theme.spacing(2)
  },
  tableContainer: {
    marginTop: 16,
    border: '1px solid black',
    height: '60vh',
    overflow: 'auto'
  },
  table: {
    width: '100%',
    minWidth: 800,
    tableLayout: 'fixed'
  },
  tableCell: {
    padding: 8,
    textAlign: 'center',
    whiteSpace: 'nowrap',
    zIndex: 0
  },
  tableHeaderCell: {
    '& .MuiTableCell-head': {
      textAlign: 'center',
      fontWeight: 'bold',
      position: 'sticky'
    }
  },
  tableRow: {
    '&:nth-of-type(odd)': {
      backgroundColor: '#e0e0e0'
    },
    '&:nth-of-type(even)': {
      backgroundColor: '#ffffff'
    },
    '&:hover': {
      background: '#bdbdbd',
      '& $stickyColumn': {
        background: '#bdbdbd'
      }
    }
  },
  stickyColumn: {
    position: 'sticky',
    left: 0,
    zIndex: 11,
    backgroundColor: '#f0f0f0',
    border: '1px solid black',
    '&:hover, $tableRow:hover &': {
      background: '#bdbdbd'
    }
  },
  col: { width: 133 }
}));

interface Props {
  visible: boolean;
  onHandleClose: () => void;
  record: TapTapStatusReport[];
  date: string | undefined;
  branch: string;
  status: string;
}

export const TaptapEodModal = ({
  visible,
  onHandleClose,
  record,
  date,
  branch,
  status
}: Props) => {
  const classes = useStyles();
  const navigate = useNavigate();

  const onTransactionNumberPress = (transactionNumber: string | undefined) => {
    navigate(`/app/transaction/${transactionNumber}`);
  };

  return (
    <Modal
      open={visible}
      onClose={onHandleClose}
      className={classes.modal}
      disableBackdropClick
    >
      <div className={classes.body}>
        <div className={classes.titleContainer}>
          <Typography variant="h5" color="textPrimary">
            {status} for {formatDate(date)} {branch}
          </Typography>
          <IconButton className={classes.closeButton} onClick={onHandleClose}>
            <CloseIcon />
          </IconButton>
        </div>

        <PerfectScrollbar>
          <TableContainer className={classes.tableContainer}>
            <Table stickyHeader className={classes.table}>
              <TableHead style={{ textAlign: 'center' }}>
                <TableRow className={`${classes.tableHeaderCell}`}>
                  <TableCell className={classes.col}>Date Created</TableCell>
                  <TableCell className={classes.col}>
                    Transaction Number
                  </TableCell>
                  <TableCell className={classes.col}>Status</TableCell>
                  <TableCell className={classes.col}>
                    Sales Representative
                  </TableCell>
                  <TableCell className={classes.col}>Assigned Technician</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {record?.map((item) => (
                  <TableRow key={item.id} className={classes.tableRow}>
                    <TableCell className={classes.tableCell}>
                      {formatDate(item.build_started_at)}
                    </TableCell>
                    <TableCell
                      className={classes.tableCell}
                      style={{
                        display: 'block',
                        width: '100%',
                        height: '100%',
                        padding: '8px',
                        fontWeight: 'bold',
                        cursor: item?.internal_transaction_no
                          ? 'pointer'
                          : 'default'
                      }}
                      onClick={() => {
                        if (item?.internal_transaction_no) {
                          onTransactionNumberPress(
                            item.internal_transaction_no
                          );
                        }
                      }}
                    >
                      {item.internal_transaction_no ?? '-'}
                    </TableCell>
                    <TableCell className={classes.tableCell}>
                      {item.status}
                    </TableCell>
                    <TableCell className={classes.tableCell}>
                      {item.sales_rep ?? '-'}
                    </TableCell>
                    <TableCell className={classes.tableCell}>
                      {item.tech ?? '-'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </PerfectScrollbar>
      </div>
    </Modal>
  );
};
